import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react'

type ToastType = 'success' | 'error' | 'warning' | 'info'

interface ToastItem {
  id: number
  message: string
  type: ToastType
}

interface ToastContextType {
  success: (message: string) => void
  error: (message: string) => void
  warning: (message: string) => void
  info: (message: string) => void
}

const ToastContext = createContext<ToastContextType | null>(null)

let nextId = 0

const TOAST_DURATION = 4000

const icons: Record<ToastType, string> = {
  success: '\u2713',
  error: '\u2717',
  warning: '!',
  info: 'i',
}

const styles: Record<ToastType, { bg: string; border: string; icon: string; text: string }> = {
  success: {
    bg: 'bg-green-600 dark:bg-green-700',
    border: 'border-green-700 dark:border-green-500',
    icon: 'bg-white/20 text-white',
    text: 'text-white',
  },
  error: {
    bg: 'bg-red-600 dark:bg-red-700',
    border: 'border-red-700 dark:border-red-500',
    icon: 'bg-white/20 text-white',
    text: 'text-white',
  },
  warning: {
    bg: 'bg-yellow-500 dark:bg-yellow-600',
    border: 'border-yellow-600 dark:border-yellow-400',
    icon: 'bg-white/20 text-white',
    text: 'text-white',
  },
  info: {
    bg: 'bg-blue-600 dark:bg-blue-700',
    border: 'border-blue-700 dark:border-blue-500',
    icon: 'bg-white/20 text-white',
    text: 'text-white',
  },
}

const ToastItem = ({ toast, onRemove }: { toast: ToastItem; onRemove: (id: number) => void }) => {
  const [isExiting, setIsExiting] = useState(false)
  const s = styles[toast.type]

  useEffect(() => {
    const exitTimer = setTimeout(() => setIsExiting(true), TOAST_DURATION - 300)
    const removeTimer = setTimeout(() => onRemove(toast.id), TOAST_DURATION)
    return () => {
      clearTimeout(exitTimer)
      clearTimeout(removeTimer)
    }
  }, [toast.id, onRemove])

  return (
    <div
      onClick={() => onRemove(toast.id)}
      className={`flex items-center gap-4 px-5 py-4 rounded-card border-2 shadow-2xl max-w-md w-full cursor-pointer transition-all duration-300 ${s.bg} ${s.border} ${
        isExiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'
      }`}
    >
      <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${s.icon}`}>
        {icons[toast.type]}
      </span>
      <p className={`text-base font-semibold flex-1 ${s.text}`}>{toast.message}</p>
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(toast.id) }}
        className={`flex-shrink-0 ${s.text} hover:opacity-70 transition-opacity text-xl leading-none`}
      >
        &times;
      </button>
    </div>
  )
}

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const addToast = useCallback((message: string, type: ToastType) => {
    const id = ++nextId
    setToasts((prev) => [...prev, { id, message, type }])
  }, [])

  const value: ToastContextType = {
    success: useCallback((msg: string) => addToast(msg, 'success'), [addToast]),
    error: useCallback((msg: string) => addToast(msg, 'error'), [addToast]),
    warning: useCallback((msg: string) => addToast(msg, 'warning'), [addToast]),
    info: useCallback((msg: string) => addToast(msg, 'info'), [addToast]),
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast container */}
      <div className="fixed top-20 right-6 z-[100] flex flex-col gap-3">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export const useToast = (): ToastContextType => {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
